import classNames from 'classnames';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { isNullOrUndefined, predicateString } from 'utilities/isType.js';

interface IProps {
  headers: ReactElement[] | string[];
  renderActiveTab: (i: number) => ReactNode;
  initialTab?: number | string | ReactNode;
  className?: string;
}

const getElementWarnings = (header: ReactElement | string) => {
  if (!predicateString(header)) {
    const hasWarning = header?.props?.tab?.hasWarning || header?.props?.hasWarning;
    return hasWarning;
  }
  return undefined;
};

const CreateTabHeaders = ({
  headers,
  activeTabIndex,
  setActiveTab,
  classes,
}: {
  headers: ReactElement[] | string[];
  activeTabIndex: number;
  setActiveTab: (index: number) => void;
  classes?: string;
}) => {
  if (headers.length <= 1) {
    return null;
  }

  // The has-warning class is triggered based on if the the component passed into headers
  // has a hasWarning boolean property
  // For example:
  // return <TabHeader day={day} hasWarning={hasWarning} key={idx} />;
  const renderedHeaderItems = headers.map((header: ReactElement | string, i: number) => {
    // @NOTE: This is done similar to the common/accordion component
    // Changes made here should be made there as well
    // @TODO Look into 1 way of doing this
    const hasWarning = getElementWarnings(header);

    return (
      <div
        key={i}
        className={classNames('Tab-item', { 'is-active': activeTabIndex === i, 'has-warning': hasWarning })}
        onClick={() => setActiveTab(i)}
      >
        {header}
      </div>
    );
  });

  return <div className={classNames('Tab', classes)}>{renderedHeaderItems}</div>;
};

export const Tabs = ({ initialTab, headers, renderActiveTab, className }: IProps) => {
  const getActiveTabIndex = () => {
    let initial = 0;

    if (!isNullOrUndefined(initialTab)) {
      if (typeof initialTab === 'number') {
        initial = initialTab;
      } else if (typeof initialTab === 'string') {
        const stringHeaders = headers;
        const idx = stringHeaders.findIndex((h) => h === initialTab);

        if (idx !== -1) {
          initial = idx;
        }
      }
    }

    return initial;
  };

  const [activeTabIndex, setActiveTabIndex] = useState(getActiveTabIndex());

  useEffect(() => {
    setActiveTabIndex(getActiveTabIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab]);

  const setActiveTab = (index: number) => {
    if (index === activeTabIndex) {
      return;
    }

    setActiveTabIndex(index);
  };

  const classes = className ? ` ${className}` : '';

  return (
    <>
      <CreateTabHeaders
        headers={headers}
        activeTabIndex={activeTabIndex}
        setActiveTab={setActiveTab}
        classes={classes}
      />
      {renderActiveTab(activeTabIndex)}
    </>
  );
};
